import React, { MouseEventHandler, useState } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { setTheme } from 'actionCreators';
import { THEMES } from '@constants';
import { green, grey, indigo } from '@mui/material/colors';
import { Link } from 'react-router-dom';
import { usePlusButtonMenu } from '../hooks/usePlusButtonMenu';

import { Box, Button, Drawer, Fab as MuiFab, Grid, ListItem, Typography } from '@mui/material';
import { Alert } from '@mui/material';
import { Palette as PaletteIcon } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { AppStateType } from 'reducers';

type DemoButtonType = {
  active?: boolean;
};

const DemoButton = styled.div<DemoButtonType>`
  cursor: pointer;
  background: ${(props) => props.theme.palette.background.paper};
  height: 80px;
  border-radius: 0.3rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.825rem;
  position: relative;
  border: 1px solid
    ${(props) =>
      !props.active ? props.theme.palette.action.selected : props.theme.palette.action.active};
`;

type DemoButtonInnerType = {
  selectedTheme: string;
};

const DemoButtonInner = styled.div<DemoButtonInnerType>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px ${(props) => props.theme.palette.action.selected};
  position: relative;

  ${(props) =>
    props.selectedTheme === THEMES.DEFAULT &&
    css`
      background: linear-gradient(-45deg, #23303f 50%, ${grey[100]} 0);
    `}
  ${(props) =>
    props.selectedTheme === THEMES.DARK &&
    css`
      background: #23303f;
    `}
  ${(props) =>
    props.selectedTheme === THEMES.LIGHT &&
    css`
      background: ${grey[100]};
    `}
  ${(props) =>
    props.selectedTheme === THEMES.BLUE &&
    css`
      background: linear-gradient(-45deg, #4782da 50%, ${grey[100]} 0);
    `}
  ${(props) =>
    props.selectedTheme === THEMES.GREEN &&
    css`
      background: linear-gradient(-45deg, ${green[500]} 50%, ${grey[100]} 0);
    `}
  ${(props) =>
    props.selectedTheme === THEMES.INDIGO &&
    css`
      background: linear-gradient(-45deg, ${indigo[500]} 50%, ${grey[100]} 0);
    `}
`;

const DemoTitle = styled(Typography)`
  text-align: center;
`;

const Fab = styled(MuiFab)`
  position: fixed;
  right: ${(props) => props.theme.spacing(8)}px;
  bottom: ${(props) => props.theme.spacing(8)}px;
  z-index: 1000;
`;

const Wrapper = styled.div`
  width: 258px;
  overflow-x: hidden;
`;

const Heading = styled(ListItem)`
  font-size: ${(props) => props.theme.typography.h5.fontSize};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  font-family: ${(props) => props.theme.typography.fontFamily};
  min-height: 56px;

  ${(props) => props.theme.breakpoints.up('sm')} {
    min-height: 64px;
  }
`;

type DemoTypes = {
  title: string;
  theme: string;
};

function Demo({ title, theme }: DemoTypes) {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state: AppStateType) => state.themeReducer);

  return (
    <Grid size={6}>
      <DemoButton
        active={theme === currentTheme.currentTheme}
        onClick={() => dispatch(setTheme(theme))}
      >
        <DemoButtonInner selectedTheme={theme} />
      </DemoButton>
      <DemoTitle variant="subtitle2" gutterBottom>
        {title}
      </DemoTitle>
    </Grid>
  );
}

function Demos() {
  return (
    <Wrapper>
      <Heading>Select a demo</Heading>

      <Box
        sx={{
          px: 4,
          my: 3,
        }}
      >
        <Alert icon={false} severity="info">
          <strong>Hello!</strong> Select your style below. Choose the ones that best fits your
          needs.
        </Alert>
      </Box>

      <Box
        sx={{
          px: 4,
          my: 3,
        }}
      >
        <Grid container spacing={3}>
          <Demo title="Dark" theme={THEMES.DARK} />
          <Demo title="Light" theme={THEMES.LIGHT} />
          <Demo title="Default" theme={THEMES.DEFAULT} />
          <Demo title="Blue" theme={THEMES.BLUE} />
          <Demo title="Green" theme={THEMES.GREEN} />
          <Demo title="Indigo" theme={THEMES.INDIGO} />
        </Grid>
      </Box>

      <Box
        sx={{
          my: 3,
          mx: 4,
        }}
      >
        <Button
          component={Link}
          nativeButton={false}
          to="/documentation/welcome"
          variant="outlined"
          color="inherit"
          size="large"
          target="_blank"
          fullWidth={true}
        >
          Documentation
        </Button>
      </Box>
      <Box
        sx={{
          my: 3,
          mx: 4,
        }}
      >
        <Button
          href="https://material-ui.com/store/items/material-app/"
          variant="contained"
          color="primary"
          size="large"
          target="_blank"
          fullWidth={true}
        >
          Get Mosaic Galaxy
        </Button>
      </Box>
    </Wrapper>
  );
}

/** -------------------------------------------------------------------------------- */

function Settings() {
  const [state, setState] = useState({
    isOpen: false,
  });

  const { openPlusButtonMenu } = usePlusButtonMenu();

  const handlePlusIconClick: MouseEventHandler<HTMLElement> = (e) => {
    openPlusButtonMenu({ target: e.currentTarget });
  };

  const toggleDrawer = (open: boolean) => () => {
    setState({ ...state, isOpen: open });
  };

  return (
    <React.Fragment>
      <Fab
        style={{ display: 'none' }}
        color="primary"
        aria-label="Edit"
        onClick={toggleDrawer(true)}
      >
        <PaletteIcon />
      </Fab>
      <Fab
        style={{ right: '40px', bottom: '30px' }}
        color="primary"
        aria-label="Add options"
        onClick={handlePlusIconClick}
      >
        <AddIcon />
      </Fab>
      <Drawer anchor="right" open={state.isOpen} onClose={toggleDrawer(false)}>
        <Demos />
      </Drawer>
    </React.Fragment>
  );
}

export default Settings;
